import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { closeDialog } from '@/components/Dialog';
import { useConnectedFormComponents } from '@/components/form';
import AppError from '@/interfaces/AppError';
import { IMaintenance } from '@/interfaces/maintenance/IMaintenance';
import maintenancesService from '@/services/maintenances';

type Form = {
  vehicleMileage: number;
  occurredAt: Date;
};

/**
 * Dates are compared at minute precision. The typed date-time input parses to
 * whole minutes while a stored timestamp may carry seconds, so a stricter
 * comparison rejects two times the UI renders identically.
 */
const toMinutes = (date: Date) => Math.floor(date.getTime() / 60000);

const schema = Yup.object().shape({
  vehicleMileage: Yup.number().min(1).required('vehicleMileage is required'),
  occurredAt: Yup.date()
    .typeError('Enter a valid date and time')
    .required('Date is required')
    .test(
      'not-in-the-future',
      'Date cannot be in the future',
      value => !value || value.getTime() <= Date.now()
    ),
});

interface Props {
  maintenanceId: string;
  isFinish?: boolean;
  /**
   * The maintenance's actual start date, when it already has one. Bounds the
   * finish date so a back-dated finish cannot land before the start.
   */
  startedAt?: string;
  onUpdate(updatedMaintenance: IMaintenance): void;
}

export default function MaintenanceStateForm({
  maintenanceId,
  isFinish,
  startedAt,
  onUpdate,
}: Props) {
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const { handleSubmit, control, setError, formState, ...formHook } =
    useForm<Form>({
      defaultValues: { occurredAt: new Date() },
      resolver: yupResolver(schema),
    });

  // Read during render so react-hook-form tracks it. Used to tell an untouched
  // default apart from a date the operator actually chose.
  const { dirtyFields } = formState;

  const { TextInput, DateTimePicker } = useConnectedFormComponents<Form>({
    control,
  });

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (data: Form) => {
    if (
      isFinish &&
      startedAt &&
      toMinutes(data.occurredAt) < toMinutes(new Date(startedAt))
    ) {
      setError('occurredAt', {
        message: 'Finish date cannot be before the start date',
      });
      return;
    }

    // Only send a timestamp the operator actually chose. Leaving the untouched
    // default out keeps the server's clock authoritative, so a workstation
    // running fast cannot have its own "now" rejected as a future date.
    const occurredAt = dirtyFields.occurredAt ? data.occurredAt : undefined;

    try {
      setSubmitLoading(true);

      const updatedMaintenance = isFinish
        ? await maintenancesService.finishMaintenance({
            maintenanceId,
            vehicleMileage: data.vehicleMileage,
            finishedAt: occurredAt,
          })
        : await maintenancesService.startMaintenance({
            maintenanceId,
            vehicleMileage: data.vehicleMileage,
            startedAt: occurredAt,
          });

      onUpdate(updatedMaintenance);
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error instanceof AppError
          ? error?.message
          : 'Error on maintenance. Please, try again later',
        { variant: 'error' }
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <FormProvider
      control={control}
      handleSubmit={handleSubmit}
      setError={setError}
      formState={formState}
      {...formHook}
    >
      <Box
        sx={{ width: { sm: '350px' } }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextInput
          label="Vehicle mileage"
          fullWidth
          fieldName="vehicleMileage"
          required
        />

        <DateTimePicker
          fieldName="occurredAt"
          label={isFinish ? 'Finished at' : 'Started at'}
          disableFuture
          minDateTime={isFinish && startedAt ? new Date(startedAt) : undefined}
          textFieldProps={{
            required: true,
            helperText:
              'Leave as-is to use the current time, or set a past date to log work that already happened',
          }}
        />

        <DialogActions>
          <Button onClick={closeDialog} disabled={submitLoading}>
            Cancel
          </Button>

          <Button
            type="submit"
            color="primary"
            disabled={submitLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {submitLoading ? 'Loading...' : 'Submit'}
          </Button>
        </DialogActions>
      </Box>
    </FormProvider>
  );
}
